import {
  AiRmrMeasuredValue,
  AiRmrOverviewMetric,
  AiRmrSummary,
} from "./aiRmrSummaryTypes";
import {
  parseAiSummaryNutrition,
  parseAiSummaryRecommendations,
  parseAiSummaryTrainingPlan,
} from "./aiSummaryResponse";

type UnknownRecord = Record<string, unknown>;

const MEASURED_VALUE_LABELS: Record<
  string,
  { label: string; defaultUnit?: string }
> = {
  RMR: { label: "RMR" },
  RER_RESTING: { label: "RER", defaultUnit: "ratio" },
  FVC: { label: "FVC" },
  BF_RESTING: { label: "Breathing frequency" },
  VEVCO2_RESTING: { label: "VE/VCO₂", defaultUnit: "slope" },
  VT_RESTING: { label: "Tidal volume" },
};

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getCleanString = (value: unknown): string =>
  getString(value).replace(/^:\s*/, "");

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
};

const parseJsonString = (value: string): unknown => {
  const normalized = value
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");

  try {
    return JSON.parse(normalized);
  } catch (error) {
    return value;
  }
};

const isRmrPayload = (value: UnknownRecord): boolean =>
  "hero" in value ||
  "patient" in value ||
  "overview_metrics" in value ||
  "measured_values" in value ||
  "nutrition" in value ||
  "workout" in value ||
  "services" in value ||
  "recommendations" in value;

const unwrapResponse = (payload: unknown): unknown => {
  let current =
    typeof payload === "string" ? parseJsonString(payload) : payload;

  for (let depth = 0; depth < 3; depth += 1) {
    if (!isRecord(current) || isRmrPayload(current)) {
      return current;
    }

    const nested = current.data ?? current.response ?? current.result;
    if (nested === undefined || nested === current) {
      return current;
    }

    current = typeof nested === "string" ? parseJsonString(nested) : nested;
  }

  return current;
};

const humanizeIdentifier = (value: string): string =>
  value
    .replace(/_(RESTING|RMR)$/i, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const parseOverviewMetrics = (value: unknown): AiRmrOverviewMetric[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiRmrOverviewMetric[]>((metrics, item) => {
    if (!isRecord(item)) {
      return metrics;
    }

    const metricValue = getFiniteNumber(item.value);
    const label = getString(item.label);
    const scoreCategory = getFiniteNumber(item.score_category);
    const scoreCategoryLabel = getString(item.score_category_label);

    // The RMR service uses zero-valued entries as placeholders for unavailable metrics.
    const isUnavailablePlaceholder =
      metricValue === 0 &&
      (!scoreCategory || scoreCategory === 0) &&
      !scoreCategoryLabel;
    if (
      metricValue === undefined ||
      metricValue < 0 ||
      isUnavailablePlaceholder ||
      !label
    ) {
      return metrics;
    }

    metrics.push({
      id: getString(item.id) || label,
      label,
      value: Math.max(0, Math.min(100, metricValue)),
      unit: getString(item.unit),
      ...(scoreCategory !== undefined ? { scoreCategory } : {}),
      scoreCategoryLabel,
    });

    return metrics;
  }, []);
};

const parseMeasuredValues = (value: unknown): AiRmrMeasuredValue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiRmrMeasuredValue[]>((measurements, item) => {
    if (!isRecord(item)) {
      return measurements;
    }

    const id = getString(item.id).toUpperCase();
    const measurementValue = getFiniteNumber(item.value);
    if (
      !id ||
      id.includes("SDNN") ||
      measurementValue === undefined ||
      measurementValue <= 0
    ) {
      return measurements;
    }

    const presentation = MEASURED_VALUE_LABELS[id];
    measurements.push({
      id,
      label: presentation?.label ?? humanizeIdentifier(id),
      value: measurementValue,
      unit: getString(item.unit) || presentation?.defaultUnit || "",
    });

    return measurements;
  }, []);
};

export const parseAiRmrSummaryResponse = (payload: unknown): AiRmrSummary => {
  const unwrapped = unwrapResponse(payload);
  const root = isRecord(unwrapped) ? unwrapped : {};
  const hero = isRecord(root.hero) ? root.hero : {};
  const rmr = isRecord(hero.rmr) ? hero.rmr : {};
  const patient = isRecord(root.patient) ? root.patient : {};
  const recommendations = isRecord(root.recommendations)
    ? root.recommendations
    : {};
  const services = isRecord(root.services) ? root.services : {};
  const rootNutrition = parseAiSummaryNutrition(root.nutrition);
  const recommendationNutrition = parseAiSummaryNutrition(
    recommendations.nutrition
  );
  const rmrValue = getFiniteNumber(rmr.value);
  const fatPercent = getFiniteNumber(hero.fat_pct);
  const carbPercent = getFiniteNumber(hero.carb_pct);
  const biologicalAge = getFiniteNumber(patient.biological_age);
  const chronologicalAge = getFiniteNumber(patient.chronological_age);

  return {
    hero: {
      ...(rmrValue !== undefined && rmrValue > 0
        ? {
            rmr: {
              value: rmrValue,
              unit: getString(rmr.unit),
            },
          }
        : {}),
      ...(fatPercent !== undefined ? { fatPercent } : {}),
      ...(carbPercent !== undefined ? { carbPercent } : {}),
    },
    patient: {
      name: getCleanString(patient.name),
      testDate: getCleanString(patient.test_date),
      companyName: getCleanString(patient.company_name),
      ...(biologicalAge !== undefined ? { biologicalAge } : {}),
      ...(chronologicalAge !== undefined ? { chronologicalAge } : {}),
    },
    legend: getString(root.legend),
    reportType: getString(root.report_type),
    bioAgeDescription: getString(root.bio_age_description),
    rmrDescription: getString(root.rmr_description),
    fuelDescription: getString(root.fuel_description),
    overviewMetrics: parseOverviewMetrics(root.overview_metrics),
    measuredValues: parseMeasuredValues(root.measured_values),
    nutrition: {
      workoutDays:
        rootNutrition.workoutDays ?? recommendationNutrition.workoutDays,
      nonWorkoutDays:
        rootNutrition.nonWorkoutDays ?? recommendationNutrition.nonWorkoutDays,
    },
    trainingPlan: parseAiSummaryTrainingPlan(
      root.workout ?? recommendations.exercise
    ),
    recommendations: parseAiSummaryRecommendations(recommendations, services),
    disclaimer: getString(root.disclaimer),
  };
};
